import React, { useRef, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Modal, ModalBodyFooter, Button, ModalProcessing, ModalLoading, ErrorMessage } from '../../custom-essentials';
import { getTimeObject, formatDateApi, formatDateFull, formatTime } from '../../functions';
import { DatePicker, SelectSingle, FormikControl, TimePicker, GroupStudentContractSelector } from '../../form';
import { TooltipWrapper } from '../../display';

import {
    validationSchema, getStudentHeader,
    getInitStudentOption, getInitCenterOption, durationOptions, getInitDurationOption,
    weightOptions, getInitWeightOption, statusOptions, getInitStatusOption
} from './helpers';

function AppointmentsBodyFooter(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const { mode, selectedCenter, selectedDate, centerOptions, selectedAppointment,
        studentOptions, groupOptions, contractOptions, contractsByStudent, studentContracts, setStudentContracts,
        parents, loaded, submitted, handleClose, handleSubmit,
        forceDelete, filterStudentsByGroup, getContractsForStudent, getContractsForGroup, isReadingPlex } = props;

    // Get initial values function
    const getInitialValues = useMemo(() => {
        if (mode === 'delete') {
            return {};
        }

        // Get initial group option for ReadingPlex
        const getInitGroupOption = () => {
            if (!isReadingPlex || !groupOptions || groupOptions.length === 0) {
                return { value: -1, label: 'All Groups' };
            }
            return groupOptions[0] || { value: -1, label: 'All Groups' };
        };

        // Get initial contract option for ReadingPlex
        const getInitContractOption = () => {
            if (!isReadingPlex || !contractOptions || contractOptions.length === 0) {
                return { value: -1, label: 'No Contract' };
            }
            
            // When editing an appointment, check if it has a contract
            if (selectedAppointment) {
                const appointmentContractId = selectedAppointment.contract;
                
                // If contract is null, 0, or undefined, return "No Contract"
                if (!appointmentContractId || appointmentContractId === 0 || appointmentContractId === '0') {
                    return { value: -1, label: 'No Contract', obj: null };
                }
                
                // Find matching contract
                const matchingContract = contractOptions.find(opt => 
                    opt.value && (parseInt(opt.value) === parseInt(appointmentContractId))
                );
                if (matchingContract) {
                    return matchingContract;
                }
                
                // If contract ID doesn't match any option, default to "No Contract"
                return { value: -1, label: 'No Contract', obj: null };
            }
            
            // Default to "No Contract" for new appointments (user can change it)
            return { value: -1, label: 'No Contract', obj: null };
        };

        return {
            date: formatDateApi(selectedAppointment?.date || selectedDate?.date || new Date()),
            time: getTimeObject(parseInt(selectedAppointment?.time || 840)),
            center: getInitCenterOption(selectedAppointment?.center || selectedCenter?.value, centerOptions),
            group: getInitGroupOption(),
            contract: getInitContractOption(),
            student: getInitStudentOption(selectedAppointment?.student, studentOptions),
            duration: getInitDurationOption(selectedAppointment?.duration),
            weight: isReadingPlex ? null : getInitWeightOption(selectedAppointment?.weight),
            status: getInitStatusOption(selectedAppointment?.status),
            specialNotes: selectedAppointment?.special_notes || '',
            specialNotes2: selectedAppointment?.special_notes_2 || '',
            nWeeks: selectedAppointment?.n_weeks || 1, // ReadingPlex: number of weeks to schedule
        };
    }, [mode, selectedAppointment, selectedDate, selectedCenter, centerOptions, groupOptions, contractOptions, studentOptions, isReadingPlex]);

    // Create portal-specific validation schema (weight not required for ReadingPlex)
    const portalValidationSchema = useMemo(() => {
        if (mode === 'delete') return Yup.object({});
        if (isReadingPlex) {
            // For ReadingPlex, make weight optional
            return validationSchema.shape({
                weight: Yup.mixed().nullable().optional()
            });
        }
        return validationSchema;
    }, [mode, isReadingPlex]);

    // Hooks must be called before any conditional returns
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues,
        validationSchema: portalValidationSchema,
        onSubmit: handleSubmit
    });

    // Preserve center value when selectedCenter prop doesn't change
    // This prevents the center from being reset when other dependencies of getInitialValues change
    // Only preserve in create mode - in edit mode, use the appointment's center
    const preservedCenterRef = useRef(null);
    useEffect(() => {
        if (mode === 'create' && selectedCenter?.value && centerOptions && centerOptions.length > 0) {
            const correctCenterValue = getInitCenterOption(selectedCenter.value, centerOptions);
            
            // Store the correct center value
            preservedCenterRef.current = correctCenterValue;
            
            // If the form's center value is wrong (not matching selectedCenter), restore it
            // This handles the case where enableReinitialize resets it incorrectly when group is selected
            if (formik.values.center?.value !== correctCenterValue?.value) {
                formik.setFieldValue('center', correctCenterValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, selectedCenter?.value, centerOptions]);

    // Auto-select "All Students in Group" when group is selected and studentOptions are updated
    useEffect(() => {
        console.log('[CONTRACT_INIT_EFFECT] Running with:', {
            isReadingPlex,
            hasGroup: !!formik.values.group?.value,
            groupValue: formik.values.group?.value,
            studentOptionsCount: studentOptions?.length,
            contractsByStudentKeys: contractsByStudent ? Object.keys(contractsByStudent).length : 0
        });
        
        if (isReadingPlex && formik.values.group?.value && formik.values.group?.value !== -1) {
            // Wait a bit for filterStudentsByGroup to update studentOptions
            const timer = setTimeout(() => {
                const allGroupOption = studentOptions.find(opt => opt.value === 'all_group');
                console.log('[CONTRACT_INIT_EFFECT] allGroupOption found:', !!allGroupOption);
                console.log('[CONTRACT_INIT_EFFECT] Has groupStudents:', !!allGroupOption?.object?.groupStudents);
                console.log('[CONTRACT_INIT_EFFECT] contractsByStudent available:', !!contractsByStudent);
                
                if (allGroupOption && formik.values.student?.value !== 'all_group') {
                    // Auto-select "All Students in Group"
                    formik.setFieldValue('student', allGroupOption);
                    console.log('[CONTRACT_INIT_EFFECT] Auto-selected all_group');
                    
                    // Initialize student contracts with default contracts for each student
                    if (contractsByStudent && allGroupOption.object?.groupStudents) {
                        const initialContracts = {};
                        const groupStudents = allGroupOption.object.groupStudents;
                        console.log('[CONTRACT_INIT_EFFECT] Initializing contracts for', groupStudents.length, 'students');
                        
                        groupStudents.forEach(student => {
                            const studentId = student.value;
                            const availableContracts = contractsByStudent[studentId] || [];
                            console.log('[INIT_CONTRACTS] Student:', student.label, 'Available contracts:', availableContracts.length);
                            
                            if (availableContracts.length > 0) {
                                // availableContracts is already an array of formatted options with {value, label, obj}
                                // Select the first one (already formatted)
                                const firstContract = availableContracts[0];
                                console.log('[INIT_CONTRACTS] Auto-selecting contract:', firstContract.label, 'for', student.label);
                                initialContracts[studentId] = firstContract; // Use the formatted option directly
                            } else {
                                // No contract available for this student
                                console.log('[INIT_CONTRACTS] No contracts for', student.label);
                                initialContracts[studentId] = { value: -1, label: 'No Contract', obj: null };
                            }
                        });
                        console.log('[INIT_CONTRACTS] Final initialContracts object keys:', Object.keys(initialContracts).length);
                        console.log('[INIT_CONTRACTS] Calling setStudentContracts with', Object.keys(initialContracts).length, 'students');
                        setStudentContracts(initialContracts);
                    } else {
                        console.error('[CONTRACT_INIT_EFFECT] CANNOT INITIALIZE - contractsByStudent or groupStudents missing!', {
                            hasContractsByStudent: !!contractsByStudent,
                            hasGroupStudents: !!allGroupOption?.object?.groupStudents
                        });
                    }
                }
            }, 100); // Small delay to allow state update from filterStudentsByGroup
            
            return () => clearTimeout(timer);
        } else if (isReadingPlex && (!formik.values.group?.value || formik.values.group?.value === -1)) {
            // If group is deselected, reset student if it was "all_group"
            if (formik.values.student?.value === 'all_group') {
                formik.setFieldValue('student', { value: -1, label: 'Please select a student...' });
                formik.setFieldValue('contract', { value: -1, label: 'No Contract' });
                setStudentContracts({}); // Clear student contracts
            }
        }
    }, [isReadingPlex, formik.values.group, studentOptions, contractsByStudent, getContractsForGroup]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize contract when editing an appointment with existing contract
    useEffect(() => {
        if (mode !== 'create' && isReadingPlex && selectedAppointment && selectedAppointment.contract && 
            contractOptions && contractOptions.length > 0 && formik.values.student?.value) {
            
            const appointmentContractId = selectedAppointment.contract;
            const currentContractValue = formik.values.contract?.value;
            
            // Only initialize if contract hasn't been set yet or doesn't match appointment's contract
            if (!currentContractValue || parseInt(currentContractValue) !== parseInt(appointmentContractId)) {
                // Try to find matching contract in all contract options
                const matchingContract = contractOptions.find(opt => 
                    opt.value && parseInt(opt.value) === parseInt(appointmentContractId)
                );
                
                if (matchingContract) {
                    console.log('[APPOINTMENT_CONTRACT_INIT] Initializing contract from appointment:', matchingContract.label);
                    formik.setFieldValue('contract', matchingContract);
                } else {
                    // If not found in all contracts, check student-specific contracts
                    const studentId = formik.values.student.value;
                    const availableContracts = contractsByStudent && contractsByStudent[studentId] ? contractsByStudent[studentId] : [];
                    const studentMatchingContract = availableContracts.find(opt => 
                        opt.value && parseInt(opt.value) === parseInt(appointmentContractId)
                    );
                    
                    if (studentMatchingContract) {
                        console.log('[APPOINTMENT_CONTRACT_INIT] Initializing contract from student contracts:', studentMatchingContract.label);
                        formik.setFieldValue('contract', studentMatchingContract);
                    }
                }
            }
        }
    }, [mode, isReadingPlex, selectedAppointment, contractOptions, contractsByStudent, formik.values.student]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize contract for individual student appointments (ReadingPlex only)
    useEffect(() => {
        if (isReadingPlex && formik.values.student?.value && 
            formik.values.student?.value !== -1 && 
            formik.values.student?.value !== 'all_group') {
            
            const studentId = formik.values.student.value;
            const availableContracts = contractsByStudent && contractsByStudent[studentId] ? contractsByStudent[studentId] : [];
            
            console.log('[INDIVIDUAL_CONTRACT_INIT] Student selected:', formik.values.student.label, 'Available contracts:', availableContracts.length);
            
            // Only auto-select if not editing an appointment with existing contract
            if (mode === 'create' || !selectedAppointment?.contract) {
                // Check if user has explicitly selected "No Contract" (value: -1 or 0)
                const currentContractValue = formik.values.contract?.value;
                const isNoContract = currentContractValue === -1 || currentContractValue === 0 || currentContractValue === '-1' || currentContractValue === '0';
                
                // Don't override if user has explicitly selected "No Contract"
                if (isNoContract) {
                    console.log('[INDIVIDUAL_CONTRACT_INIT] User has selected "No Contract", not overriding');
                    return;
                }
                
                // Only auto-select if contract is truly unset (not just missing obj)
                if (availableContracts.length > 0 && (!formik.values.contract || (!formik.values.contract.value && !formik.values.contract.obj))) {
                    // Auto-select first active contract if no contract is currently selected
                    const firstContract = availableContracts[0];
                    console.log('[INDIVIDUAL_CONTRACT_INIT] Auto-selecting contract:', firstContract.label);
                    formik.setFieldValue('contract', firstContract);
                } else if (availableContracts.length === 0) {
                    console.log('[INDIVIDUAL_CONTRACT_INIT] No contracts available, setting to No Contract');
                    formik.setFieldValue('contract', { value: -1, label: 'No Contract', obj: null });
                }
            }
        }
    }, [isReadingPlex, formik.values.student, contractsByStudent, mode, selectedAppointment]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!loaded) {
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <ModalLoading />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="lte-mpLRed"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else if (submitted) {
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <h4>
                        {mode === 'create' ? 'Created' : mode === 'edit' ?
                            'Updated' : mode === 'delete' ? 'Deleted' : 'Unexpected mode'} successfully!
                    </h4>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="lte-mpLRed"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else if (mode === 'delete') {
        const sa = selectedAppointment;
        const student = studentOptions.find(s => s.object?.user_id === sa.student)?.object || {};
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <h4>Delete this appointment?</h4>
                    <div className="grid grid-cols-1">
                        <div>
                            <b>Student: </b>{student ? `${student.first_name} ${student.last_name}` :
                                `Unknown student (UID: ${sa.student})`}
                        </div>
                        <div><b>Time: </b>{formatDateFull(sa.date)} at {formatTime(sa.time)}</div>
                        <div><b>Center: </b>{sa.centerName}</div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex flex-row gap-x-2 flex-wrap">
                        {formik.isSubmitting &&
                            <ModalProcessing />
                        }
                        {formik.status && !formik.isSubmitting ?
                            <div className="text-mpLRed text-sm mr-8">
                                {formik.status}
                            </div> : null
                        }
                        {formik.status ?
                            <TooltipWrapper
                                tooltipText="Deletes the appointment without updating hours, overriding most errors."
                            >
                                <Button
                                    color="lte-mpLRed"
                                    disabled={formik.isSubmitting}
                                    onClick={() => forceDelete(formik)}
                                >
                                    Force Delete
                                </Button>
                            </TooltipWrapper>
                            : null}
                        <Button
                            color="lte-mpLRed"
                            disabled={formik.isSubmitting}
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="hol-mpLRed"
                            disabled={formik.isSubmitting}
                            onClick={formik.handleSubmit}
                        >
                            Delete
                        </Button>
                    </div>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else {
        // Create/Edit mode
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <div className="flex flex-row gap-x-4 items-start">
                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                            <h4>Appointment Date</h4>
                            <DatePicker
                                id="appointment-form-datepicker"
                                name="date"
                                value={formik.values.date}
                                onChange={formik.handleChange}
                            />
                            {formik.errors.date ? (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.date}
                                </ErrorMessage>
                            ) : null}
                        </div>
                        <div className="grid grid-cols-1 gap-y-2 w-5/12">
                            <TooltipWrapper
                                tooltipText="Must be an increment of 30 minutes"
                            >
                                <h4 className="text-mpLBlue">Time</h4>
                            </TooltipWrapper>
                            <TimePicker
                                id="appointment-form-time"
                                name="time"
                                value={formik.values.time?.formatted24 || ''}
                                onChange={formik.handleChange}
                                step={1800} // 30 min
                            />
                            {formik.errors.time ? (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.time}
                                </ErrorMessage>
                            ) : null}
                        </div>
                    </div>
                    <br />

                    {/* ReadingPlex-specific: Group dropdown */}
                    {isReadingPlex && groupOptions && groupOptions.length > 0 && (
                        <>
                            <div className="flex flex-row gap-x-4">
                                <div className="grid grid-cols-1 gap-y-2 w-full">
                                    <h4>Group</h4>
                                    <SelectSingle
                                        id="appointments-group"
                                        name="group"
                                        disabled={formik.values.student?.value && formik.values.student?.value !== -1 && formik.values.student?.value !== 'all_group'}
                                        value={formik.values.group}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            const selectedGroup = e.target.value;
                                            
                                            // Filter students when group changes
                                            if (filterStudentsByGroup) {
                                                filterStudentsByGroup(selectedGroup.value);
                                            }
                                            
                                            // Reset student and contract when group changes
                                            // The useEffect will auto-select "All Students in Group" after studentOptions update
                                            if (selectedGroup.value && selectedGroup.value !== -1) {
                                                // Reset to allow useEffect to handle auto-selection
                                                formik.setFieldValue('student', { value: -1, label: 'Please select a student...' });
                                                formik.setFieldValue('contract', { value: -1, label: 'No Contract' });
                                            } else {
                                                // No group selected, reset student and contract
                                                formik.setFieldValue('student', { value: -1, label: 'Please select a student...' });
                                                formik.setFieldValue('contract', { value: -1, label: 'No Contract' });
                                            }

                                            // DON'T auto-set center - keep the currently selected/viewed center
                                            // This ensures group appointments are created at the center the user is viewing
                                            // if (selectedGroup.center && centerOptions) {
                                            //     const groupCenter = centerOptions.find(c => c.value === parseInt(selectedGroup.center));
                                            //     if (groupCenter) {
                                            //         formik.setFieldValue('center', groupCenter);
                                            //     }
                                            // }
                                        }}
                                        options={groupOptions}
                                    />
                                    {formik.values.student?.value && formik.values.student?.value !== -1 && formik.values.student?.value !== 'all_group' && (
                                        <div className="text-mpOrange text-xs">
                                            Group is disabled when an individual student is selected.
                                        </div>
                                    )}
                                    {formik.values.group?.value && formik.values.group?.value !== -1 && formik.values.student?.value === 'all_group' && (
                                        <div className="text-mpLBlue text-sm">
                                            All students in the selected group will be scheduled together.
                                        </div>
                                    )}
                                    {formik.values.group?.value > 0 && formik.values.group?.schedulingDays && (
                                        <div className="text-sm text-gray-600">
                                            <b>Scheduling Info:</b> {formik.values.group.schedulingDays} at {formatTime(formik.values.group.schedulingTime)}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <br />
                        </>
                    )}

                    <div className="flex flex-row gap-x-4">
                        <div className="grid grid-cols-1 gap-y-2 w-full">
                            {getStudentHeader(formik.values.student?.object, parents)}
                            <SelectSingle
                                id="appointments-student"
                                name="student"
                                disabled={mode !== 'create' || (isReadingPlex && formik.values.group?.value && formik.values.group?.value !== -1)}
                                value={formik.values.student}
                                onChange={(e) => {
                                    const student = e.target.value;
                                    const studentObj = student.object;
                                    const newValues = { ...formik.values };

                                    // Handle "All Students in Group" option
                                    if (student.value === 'all_group') {
                                        newValues.student = student;
                                        // Get contracts for all students in the group
                                        if (getContractsForGroup && studentObj?.groupStudents) {
                                            const groupContracts = getContractsForGroup(studentObj.groupStudents);
                                            // Auto-select first contract if available, or keep No Contract
                                            if (groupContracts.length > 1) {
                                                newValues.contract = groupContracts[1]; // First actual contract after "No Contract"
                                            } else {
                                                newValues.contract = { value: -1, label: 'No Contract' };
                                            }
                                        }
                                    } else if (studentObj) {
                                        // Individual student selected
                                        // Adjust weight to match student weight (only for MathPlex)
                                        if (!isReadingPlex) {
                                            const weightField = studentObj.mp_weight;
                                            const weightOption = getInitWeightOption(weightField);
                                            newValues.weight = weightOption;
                                        }
                                        const specialNotesField = isReadingPlex ? studentObj.rp_special_notes : studentObj.mp_special_notes;
                                        const specialNotes2Field = isReadingPlex ? studentObj.rp_special_notes_2 : studentObj.mp_special_notes_2;

                                        newValues.student = student;
                                        newValues.specialNotes = specialNotesField || '';
                                        newValues.specialNotes2 = specialNotes2Field || '';

                                        // Get contracts for this specific student
                                        if (getContractsForStudent && isReadingPlex) {
                                            const studentContracts = getContractsForStudent(student.value);
                                            // Auto-select first contract if available
                                            if (studentContracts.length > 1) {
                                                newValues.contract = studentContracts[1]; // First actual contract after "No Contract"
                                            } else {
                                                newValues.contract = { value: -1, label: 'No Contract' };
                                            }
                                        }
                                    } else {
                                        newValues.student = student;
                                        newValues.contract = { value: -1, label: 'No Contract' };
                                    }

                                    formik.setValues(newValues);
                                }}
                                options={studentOptions}
                            />
                            {formik.errors.student ? (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.student}
                                </ErrorMessage>
                            ) : null}
                            {mode !== 'create' && (
                                <div className="text-mpOrange" style={{ fontSize: "8pt" }} >
                                    Field disabled. Create a new appointment if you would like to select a different student.
                                </div>
                            )}
                            {formik.values.student?.value === 'all_group' && (
                                <div className="text-mpLBlue text-sm">
                                    All students in the selected group will be scheduled together. Student field is disabled when a group is selected.
                                </div>
                            )}
                            {isReadingPlex && formik.values.group?.value && formik.values.group?.value !== -1 && formik.values.student?.value !== 'all_group' && (
                                <div className="text-mpOrange text-xs">
                                    Please wait for all students in the group to be auto-selected...
                                </div>
                            )}
                        </div>
                    </div>
                    <br />

                    {/* ReadingPlex-specific: Contract selection */}
                    {isReadingPlex && (
                        <>
                            {/* Group Appointment: Show per-student contract selectors */}
                            {formik.values.student?.value === 'all_group' ? (
                                <div className="flex flex-row gap-x-4">
                                    <div className="grid grid-cols-1 gap-y-2 w-full">
                                        <h4>Student Contracts</h4>
                                        <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                            <GroupStudentContractSelector
                                                groupStudents={formik.values.student?.object?.groupStudents || []}
                                                contractsByStudent={contractsByStudent || {}}
                                                studentContracts={studentContracts}
                                                onContractChange={(studentId, selectedOption) => {
                                                    setStudentContracts(prev => ({
                                                        ...prev,
                                                        [studentId]: selectedOption
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="text-xs text-blue-600 mt-1">
                                            💡 Each student can have a different contract (even with different dates) as long as the contract type serves the group's purpose.
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Individual Appointment: Show single contract dropdown */
                                <div className="flex flex-row gap-x-4">
                                    <div className="grid grid-cols-1 gap-y-2 w-full">
                                        <h4>Contract</h4>
                                        <SelectSingle
                                            id="appointments-contract"
                                            name="contract"
                                            value={formik.values.contract}
                                            onChange={formik.handleChange}
                                            options={(() => {
                                                if (formik.values.student?.value && formik.values.student?.value !== -1 && formik.values.student?.value !== 'all_group') {
                                                    // For individual student, show their contracts (getContractsForStudent already includes "No Contract" as first option)
                                                    return getContractsForStudent ? getContractsForStudent(formik.values.student.value) : [{ value: -1, label: 'No Contract', obj: null }];
                                                }
                                                // Default: show all contracts with "No Contract" as first option
                                                const baseOptions = contractOptions || [];
                                                // Ensure "No Contract" is first option
                                                const hasNoContract = baseOptions.some(opt => (opt.value === -1 || opt.value === 0));
                                                if (!hasNoContract) {
                                                    return [{ value: -1, label: 'No Contract', obj: null }, ...baseOptions];
                                                }
                                                return baseOptions;
                                            })()}
                                        />
                                        {/* Show contract details for individual student */}
                                        {formik.values.contract?.value > 0 && formik.values.contract?.obj && (
                                            <div className="text-sm text-gray-600">
                                                <b>Contract Type:</b> {(() => {
                                                    // Try multiple ways to get contract type
                                                    const contractObj = formik.values.contract.obj;
                                                    const contractType = contractObj?.contract_type || contractObj?.type;
                                                    
                                                    // If not found in obj, try to extract from label
                                                    if (!contractType && formik.values.contract?.label) {
                                                        const labelMatch = formik.values.contract.label.match(/^([^-]+?)\s*-/);
                                                        if (labelMatch && labelMatch[1]) {
                                                            return labelMatch[1].trim();
                                                        }
                                                    }
                                                    
                                                    return contractType || 'N/A';
                                                })()}
                                                {formik.values.contract.obj.notes && (
                                                    <span> | <b>Notes:</b> {formik.values.contract.obj.notes}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <br />
                        </>
                    )}
                    <br />
                    
                    {/* ReadingPlex-specific: Number of Weeks field */}
                    {isReadingPlex && mode === 'create' && (
                        <>
                            <div className="flex flex-row gap-x-4">
                                <div className="grid grid-cols-1 gap-y-2 w-full">
                                    <TooltipWrapper
                                        tooltipText="Schedule this appointment for multiple weeks. For example, enter 4 to schedule the same appointment for 4 consecutive weeks."
                                    >
                                        <h4 className="text-mpLBlue">Number of Weeks</h4>
                                    </TooltipWrapper>
                                    <FormikControl
                                        id="appointments-n-weeks"
                                        name="nWeeks"
                                        type="number"
                                        value={formik.values.nWeeks}
                                        onChange={formik.handleChange}
                                        placeholder="1"
                                        min="1"
                                        max="52"
                                    />
                                    {formik.errors.nWeeks ? (
                                        <ErrorMessage color="mpLRed">
                                            {formik.errors.nWeeks}
                                        </ErrorMessage>
                                    ) : null}
                                    {formik.values.nWeeks > 1 && (
                                        <div className="text-mpLBlue text-sm">
                                            This will create {formik.values.nWeeks} appointments, one for each week starting from the selected date.
                                        </div>
                                    )}
                                </div>
                            </div>
                            <br />
                        </>
                    )}
                    
                    <div className="flex flex-row gap-x-4 items-start">
                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                            <h4>Center</h4>
                            <SelectSingle
                                id="appointments-center"
                                name="center"
                                value={formik.values.center}
                                onChange={formik.handleChange}
                                options={centerOptions}
                            />
                            {formik.errors.center ? (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.center}
                                </ErrorMessage>
                            ) : null}
                        </div>
                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                            <h4>Duration</h4>
                            <SelectSingle
                                id="appointments-duration"
                                name="duration"
                                value={formik.values.duration}
                                onChange={formik.handleChange}
                                options={durationOptions}
                            />
                            {formik.errors.duration ? (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.duration}
                                </ErrorMessage>
                            ) : null}
                        </div>
                    </div>
                    <br />
                    {/* Weight field - only for MathPlex */}
                    {!isReadingPlex && (
                        <div className="flex flex-row gap-x-4 items-start">
                            <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                <TooltipWrapper
                                    tooltipText={`This will ONLY update the weight for the selected appointment.`}
                                >
                                    <h4 className="text-mpLBlue">Weight</h4>
                                </TooltipWrapper>
                                <SelectSingle
                                    id="appointments-weight"
                                    name="weight"
                                    value={formik.values.weight}
                                    onChange={formik.handleChange}
                                    options={weightOptions}
                                />
                                {formik.errors.weight ? (
                                    <ErrorMessage color="mpLRed">
                                        {formik.errors.weight}
                                    </ErrorMessage>
                                ) : null}
                            </div>
                        </div>
                    )}
                    {!isReadingPlex && <br />}
                    <div className="flex flex-row gap-x-4 items-start">
                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                            <TooltipWrapper
                                tooltipText={
                                    <>
                                        <div>Scheduled, In Progress, Completed: Hours charged</div>
                                        <br />
                                        <div>
                                            Missed (hours charged) &amp; Cancelled (not charged):
                                            Some filters can be used to remove these entries
                                        </div>
                                        <br />
                                        <div>
                                            Not Charged: Session completed, but due to an issue,
                                            the hours were refunded
                                        </div>
                                    </>
                                }
                            >
                                <h4 className="text-mpLBlue">Status</h4>
                            </TooltipWrapper>
                            <SelectSingle
                                id="appointments-status"
                                name="status"
                                value={formik.values.status}
                                onChange={formik.handleChange}
                                options={statusOptions}
                            />
                            {formik.errors.status ? (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.status}
                                </ErrorMessage>
                            ) : null}
                        </div>
                    </div>
                    <br />
                    <div className="flex flex-row gap-x-4">
                        <div className="grid grid-cols-1 gap-y-2 w-full">
                            <TooltipWrapper
                                tooltipText={`This will ONLY update the "special notes" (red !) for the selected appointment.`}
                            >
                                <h4 className="text-mpLBlue">Special Notes</h4>
                            </TooltipWrapper>
                            <FormikControl
                                id="appointments-special-notes"
                                as="textarea"
                                name="specialNotes"
                                value={formik.values.specialNotes}
                                onChange={formik.handleChange}
                                placeholder="This will only update the special notes for this session. To adjust future notes for all of a student's future sessions, please update the student's profile."
                            />
                            {formik.errors.specialNotes ? (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.specialNotes}
                                </ErrorMessage>
                            ) : null}
                        </div>
                    </div>
                    <br />
                    <div className="flex flex-row gap-x-4">
                        <div className="grid grid-cols-1 gap-y-2 w-full">
                            <TooltipWrapper
                                tooltipText={`This will ONLY update the "special notes instructional" (blue !) for the selected appointment.`}
                            >
                                <h4 className="text-mpLBlue">Special Notes Instructional</h4>
                            </TooltipWrapper>
                            <FormikControl
                                id="appointments-special-notes-2"
                                as="textarea"
                                name="specialNotes2"
                                value={formik.values.specialNotes2}
                                onChange={formik.handleChange}
                                placeholder="This will only update the special notes instructional for this session. To adjust future notes for all of a student's future sessions, please update the student's profile."
                            />
                            {formik.errors.specialNotes2 ? (
                                <ErrorMessage color="mpLRed">
                                    {formik.errors.specialNotes2}
                                </ErrorMessage>
                            ) : null}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex flex-row gap-x-2 flex-wrap">
                        {!formik.isValid && parseInt(formik.submitCount) && !formik.isSubmitting ?
                            (
                                <div className="text-mpLRed mr-1">
                                    One or more fields needs to be corrected.
                                </div>
                            ) : null
                        }
                        {formik.isSubmitting ?
                            <ModalProcessing /> : null
                        }
                        {formik.status && !formik.isSubmitting ?
                            <div className="text-mpLRed" style={{ marginRight: "2rem" }}>
                                {formik.status}
                            </div> : null
                        }
                        <Button
                            color="lte-mpLRed"
                            onClick={handleClose}
                            disabled={formik.isSubmitting}
                        >
                            Close
                        </Button>
                        <Button
                            color="lte-mpLBlue"
                            onClick={formik.handleSubmit}
                            disabled={formik.isSubmitting}
                        >
                            Submit
                        </Button>
                    </div>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    }
}

// Set displayName and _modalComponentType so findComponentByType can find this component
AppointmentsBodyFooter.displayName = 'BodyFooter';
AppointmentsBodyFooter._modalComponentType = 'BodyFooter';

export default connect(null, {

})(AppointmentsBodyFooter);
